import {
  Component,
  computed,
  EventEmitter,
  inject,
  Input,
  Output,
  signal,
} from '@angular/core';
import { NgForOf, NgIf } from '@angular/common';
import { TableModule } from 'primeng/table';
import { TagModule } from 'primeng/tag';
import { ChipModule } from 'primeng/chip';
import { ButtonModule } from 'primeng/button';
import { CheckboxModule } from 'primeng/checkbox';
import { SelectButtonModule } from 'primeng/selectbutton';
import { FormsModule } from '@angular/forms';
import { DuplicateIndicator } from '../../../services/indicator/indicator-validator.service';
import { IndicatorStore } from '../../../stores/indicator/indicator.state';
import { Indicator } from '../../../models/indicator';
import { FullIndicatorNameComponent } from '../../atoms/full-indicator-name/full-indicator-name.component';

type FilterState = 'all' | 'withData' | 'withoutData';

@Component({
  selector: 'app-indicator-validation-list',
  standalone: true,
  imports: [
    NgForOf,
    NgIf,
    TableModule,
    TagModule,
    ChipModule,
    ButtonModule,
    CheckboxModule,
    SelectButtonModule,
    FormsModule,
    FullIndicatorNameComponent,
  ],
  templateUrl: './indicator-validation-list.component.html',
  styleUrl: './indicator-validation-list.component.scss',
})
export class IndicatorValidationListComponent {
  @Input() duplicateIndicators: DuplicateIndicator[] = [];
  @Output() deleted = new EventEmitter();
  readonly indicatorStore = inject(IndicatorStore);

  // Filter state
  filterState = signal<FilterState>('all');

  filterOptions = [
    { label: 'Alle', value: 'all' },
    { label: 'mit Daten', value: 'withData' },
    { label: 'ohne Daten', value: 'withoutData' },
  ];

  // Safety checkbox for deletion
  canSafelyDelete = signal(false);

  // Computed filtered list
  filteredIndicators = computed(() => {
    const state = this.filterState();
    const indicators = this.duplicateIndicators;

    if (state === 'all') {
      return indicators;
    } else if (state === 'withData') {
      return indicators.filter(item => item.dataCount > 0);
    } else {
      return indicators.filter(item => item.dataCount === 0);
    }
  });

  cols = [
    { field: 'isSet', header: 'Set' },
    { field: 'name', header: 'Name' },
    { field: 'key', header: 'Schlüssel' },
    { field: 'dataCount', header: 'Daten' },
    { field: 'duplicateCount', header: 'Duplikate' },
  ];

  async deleteIndicatorsWithoutData() {
    // Group indicators by key
    const indicatorsByKey = new Map<string, DuplicateIndicator[]>();
    this.duplicateIndicators.forEach(item => {
      if (!indicatorsByKey.has(item.key)) {
        indicatorsByKey.set(item.key, []);
      }
      indicatorsByKey.get(item.key)?.push(item);
    });

    const indicatorsToDelete: Indicator[] = [];

    // For each key group, decide which to delete
    indicatorsByKey.forEach((group, key) => {
      // Sort group by priority:
      // 1. Indicators with data (keep them)
      // 2. Indicators with parent (sets) - prefer to keep
      // 3. Indicators without parent and without data (delete these)
      const sorted = [...group].sort((a, b) => {
        // Priority 1: Keep those with data
        if (a.dataCount > 0 && b.dataCount === 0) return -1;
        if (a.dataCount === 0 && b.dataCount > 0) return 1;

        // Priority 2: Keep parent indicators (isSet or has children)
        const aIsParent = a.indicator.isSet || !a.indicator.parent;
        const bIsParent = b.indicator.isSet || !b.indicator.parent;
        if (aIsParent && !bIsParent) return -1;
        if (!aIsParent && bIsParent) return 1;

        return 0;
      });

      // Keep the first one (highest priority), delete the rest
      const toDelete = sorted.slice(1);
      toDelete.forEach(item => {
        indicatorsToDelete.push(item.indicator as Indicator);
      });
    });

    if (indicatorsToDelete.length === 0) {
      return;
    }

    try {
      await this.indicatorStore.removeMultiple({
        items: indicatorsToDelete,
        message: {
          severity: 'success',
          summary: 'Löschen abgeschlossen',
          detail: `${indicatorsToDelete.length} doppelte Indikatoren wurden erfolgreich gelöscht.`,
        },
      });
      // Reset checkbox after successful deletion
      this.canSafelyDelete.set(false);
      setTimeout(() => {
        this.deleted.emit();
      }, 300);
    } catch (error) {
      console.error('Error deleting indicators:', error);
    }
  }
}
